import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, Stack, styled} from '@mui/material';
import {
  Icon,
  SwitchButton,
  designSystemToken,
} from '@lightricks/react-design-system';
import ActionsMenu, {
  MenuOption,
} from '@contentlab/components/actions-menu/ActionsMenu';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import {DeleteAssetsModalReason} from '@contentlab/views/contentlab/pages/project-page/components/delete-assets-modal/DeleteAssetsModal';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import DeleteAssetsModal from '../delete-assets-modal';

const ActionsMenuContainer = styled(Box)({
  width: '24px',
  height: '24px',
  backgroundColor: designSystemToken('semantic.bg.neutral-secondary'),
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
});

interface CheckedFilesSwitchProps {
  checkedAssetIds: string[];
  setCheckedAssetIds: (assetIds: string[]) => void;
  filesCheckedOn: boolean;
  setFilesCheckedOn: (checked: boolean) => void;
  deleteAssets: (assetIds: string[]) => Promise<void>;
  downloadAssets: (assetIds: string[]) => Promise<void>;
}

function CheckedFilesSwitch(props: CheckedFilesSwitchProps) {
  const {
    checkedAssetIds,
    setCheckedAssetIds,
    filesCheckedOn,
    setFilesCheckedOn,
    deleteAssets,
    downloadAssets,
  } = props;
  const {uiInteraction} = useUiInteractionEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {viewPresented} = useViewPresentedEvent();

  const [showDeleteAssetsModal, setShowDeleteAssetsModal] = useState(false);
  const [deleteAssetsModalPresentationId, setDeleteAssetsModalPresentationId] =
    useState(uuid());
  const downloadOption = {
    id: 'download',
    title: translate('Download'),
    icon: 'Navigation-Download',
    onClick: async () => {
      if (checkedAssetIds.length === 0) {
        return;
      }

      await downloadAssets(checkedAssetIds);
    },
  };

  const deleteOption = {
    id: 'delete',
    title: translate('Delete'),
    icon: 'Actions-Remove',
    onClick: async () => {
      if (checkedAssetIds.length === 0) {
        return;
      }
      const interactionId = uuid();
      uiInteraction({
        item_name: 'delete_checked_assets',
        interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
        flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
        interaction_id: interactionId,
      });
      viewPresented({
        interaction_id: interactionId,
        view_name: CONTENT_LAB_VIEW_NAMES.DELETE_ASSETS_MODAL,
        presentation_id: deleteAssetsModalPresentationId,
        view_type: 'popup',
        view_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
        source: 'item_pressed',
      });
      setShowDeleteAssetsModal(true);
    },
  };

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const handleOnActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    uiInteraction({
      item_name: 'checked_files_menu_pressed',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
    });
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleOnActionsClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const handleDeleteModalClose = async (reason: DeleteAssetsModalReason) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: reason,
      interaction_location: CONTENT_LAB_VIEW_NAMES.DELETE_ASSETS_MODAL,
      flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
      interaction_id: interactionId,
    });
    viewDismissed({
      interaction_id: interactionId,
      presentation_id: deleteAssetsModalPresentationId,
      reason: reason === DeleteAssetsModalReason.Delete ? 'confirm' : 'cancel',
    });
    if (reason === DeleteAssetsModalReason.Delete) {
      await deleteAssets(checkedAssetIds);
      setCheckedAssetIds([]);
    }
    setDeleteAssetsModalPresentationId(uuid());
    setShowDeleteAssetsModal(false);
  };

  return (
    <Stack direction="row" alignItems="center" gap="8px">
      <SwitchButton
        label={
          filesCheckedOn
            ? translate('selected_files', {count: checkedAssetIds.length})
            : translate('Bulk select')
        }
        checked={filesCheckedOn}
        checkedTrackColor={designSystemToken('semantic.bg.neutral')}
        onChange={(event, checked) => {
          setCheckedAssetIds([]);
          setFilesCheckedOn(checked);
        }}
      />
      {filesCheckedOn && (
        <ActionsMenuContainer onClick={handleOnActionsClick}>
          <Icon
            size="small"
            appearance="neutral"
            name="Actions-More"
            style={{width: '20px', height: '20px'}}
          />
        </ActionsMenuContainer>
      )}
      <ActionsMenu
        menuOpen={!!actionsMenuAnchorEl}
        onCloseMenu={(event) => {
          event.stopPropagation();
          handleOnActionsClose();
        }}
        anchorEl={actionsMenuAnchorEl}
        menuOptions={[downloadOption, deleteOption] as MenuOption[]}
      />
      <DeleteAssetsModal
        title={translate('Delete checked videos?')}
        description={translate('They will be permanently removed.')}
        open={showDeleteAssetsModal}
        onClose={handleDeleteModalClose}
      />
    </Stack>
  );
}

export default CheckedFilesSwitch;
